import { useState } from "react";
import clsx from "clsx";
import {
  BookOpenText,
  Check,
  Clock3,
  MapPin,
  Navigation,
  ParkingCircle,
  Route,
  Sparkles,
  Users
} from "lucide-react";
import {
  XIAOHONGSHU_PARKING_TASKS,
  type XiaohongshuParkingMode,
  type XiaohongshuParkingOption,
  type XiaohongshuParkingTaskId
} from "../../config/xiaohongshuParkingDemo";
import "./NightXiaohongshuParkingDemo.css";

export function NightXiaohongshuParkingDemo() {
  const [taskId, setTaskId] = useState<XiaohongshuParkingTaskId>("shopping");
  const [mode, setMode] = useState<XiaohongshuParkingMode>("without-xhs");
  const task = XIAOHONGSHU_PARKING_TASKS[taskId];
  const answer = mode === "with-xhs" ? task.withXhs : task.withoutXhs;

  function selectTask(nextTaskId: XiaohongshuParkingTaskId) {
    setTaskId(nextTaskId);
    setMode("without-xhs");
  }

  return (
    <section className="xhs-parking-demo" aria-label="小红书停车能力对比演示">
      <div className="xhs-parking-demo__topline">
        <span><ParkingCircle size={30} /> 停车智选实验</span>
        <strong>同一个问题 · 两种答案</strong>
      </div>

      <aside className="xhs-parking-demo__panel">
        <header className="xhs-parking-demo__panel-header">
          <span className="xhs-parking-demo__app-icon"><ParkingCircle size={56} /></span>
          <span>
            <strong>停车助手</strong>
            <small>{mode === "with-xhs" ? "地图 + 可追溯到访证据" : "仅地图公开数据"}</small>
          </span>
        </header>

        <div className="xhs-parking-demo__task-tabs" aria-label="选择任务">
          {(Object.keys(XIAOHONGSHU_PARKING_TASKS) as XiaohongshuParkingTaskId[]).map((candidateId) => (
            <button
              className={clsx(candidateId === taskId && "is-active")}
              key={candidateId}
              onClick={() => selectTask(candidateId)}
            >
              {XIAOHONGSHU_PARKING_TASKS[candidateId].tabLabel}
            </button>
          ))}
        </div>

        <div className="xhs-parking-demo__mode-switch" aria-label="选择数据能力">
          <button
            className={clsx(mode === "without-xhs" && "is-active")}
            onClick={() => setMode("without-xhs")}
          >
            未引入小红书
          </button>
          <button
            className={clsx(mode === "with-xhs" && "is-active")}
            onClick={() => setMode("with-xhs")}
          >
            <Sparkles size={20} /> 引入小红书
          </button>
        </div>

        <div className="xhs-parking-demo__query">
          <small>用户任务</small>
          <strong>{task.title}</strong>
          <span>“{task.query}”</span>
        </div>

        <div className="xhs-parking-demo__answer">
          <small>系统回答</small>
          <strong>{answer.headline}</strong>
          <p>{answer.summary}</p>
        </div>

        <div className="xhs-parking-demo__options">
          {answer.options.map((option, index) => (
            <ParkingOptionCard
              enhanced={mode === "with-xhs"}
              index={index}
              key={option.id}
              option={option}
            />
          ))}
        </div>

        <div className={clsx(
          "xhs-parking-demo__explain",
          mode === "with-xhs" && "xhs-parking-demo__explain--enhanced"
        )}>
          {mode === "with-xhs" ? <BookOpenText size={24} /> : <MapPin size={24} />}
          <span>
            <strong>{mode === "with-xhs" ? "新增的决策信息" : "当前能力边界"}</strong>
            <small>{mode === "with-xhs" ? task.withXhs.insight : task.withoutXhs.limitation}</small>
          </span>
        </div>
      </aside>

      <main className="xhs-parking-demo__visual">
        <div className="xhs-parking-demo__visual-header">
          <span className={clsx(
            "xhs-parking-demo__source-pill",
            mode === "with-xhs" && "xhs-parking-demo__source-pill--enhanced"
          )}>
            {mode === "with-xhs"
              ? <><BookOpenText size={22} /> {task.withXhs.evidenceSummary}</>
              : <><MapPin size={22} /> 仅使用地图数据</>}
          </span>
          <span className="xhs-parking-demo__demo-label">演示数据</span>
        </div>

        <ParkingRouteVisual taskId={taskId} mode={mode} />

        <div className="xhs-parking-demo__capability">
          <span>
            <MapPin size={28} />
            <small>地图数据</small>
            <strong>位置 · 距离 · 路线</strong>
          </span>
          <i>+</i>
          <span className={clsx(mode === "with-xhs" && "is-active")}>
            <Users size={28} />
            <small>到访经验</small>
            <strong>优惠 · 满位 · 避坑</strong>
          </span>
          <i>=</i>
          <span className={clsx("xhs-parking-demo__capability-result", mode === "with-xhs" && "is-active")}>
            <Sparkles size={28} />
            <small>停车决策</small>
            <strong>{mode === "with-xhs" ? "提前规划完整方案" : "只推荐一个地点"}</strong>
          </span>
        </div>

        {mode === "with-xhs" ? (
          <p className="xhs-parking-demo__caveat">
            <Clock3 size={20} />
            {task.withXhs.caveat}
          </p>
        ) : null}
      </main>
    </section>
  );
}

function ParkingOptionCard({
  enhanced,
  index,
  option
}: {
  enhanced: boolean;
  index: number;
  option: XiaohongshuParkingOption;
}) {
  return (
    <article className={clsx(
      "xhs-parking-demo__option",
      option.recommended && "xhs-parking-demo__option--recommended"
    )}>
      <span className="xhs-parking-demo__option-rank">
        {enhanced ? index + 1 : <ParkingCircle size={26} />}
      </span>
      <span className="xhs-parking-demo__option-copy">
        <span>
          <strong>{option.name}</strong>
          <em>{option.badge}</em>
        </span>
        <small>{option.price} · {option.walk}</small>
        <p>{option.detail}</p>
        {option.evidence ? <em className="xhs-parking-demo__evidence">{option.evidence}</em> : null}
      </span>
      {option.recommended ? <Check size={24} /> : null}
    </article>
  );
}

function ParkingRouteVisual({
  mode,
  taskId
}: {
  mode: XiaohongshuParkingMode;
  taskId: XiaohongshuParkingTaskId;
}) {
  const isEnhanced = mode === "with-xhs";
  const isHospital = taskId === "hospital";

  return (
    <div className={clsx(
      "xhs-parking-demo__map",
      isEnhanced && "xhs-parking-demo__map--enhanced",
      isHospital && "xhs-parking-demo__map--hospital"
    )}>
      <div className="xhs-parking-demo__road xhs-parking-demo__road--one" />
      <div className="xhs-parking-demo__road xhs-parking-demo__road--two" />
      <div className="xhs-parking-demo__road xhs-parking-demo__road--three" />
      <div className="xhs-parking-demo__block xhs-parking-demo__block--one" />
      <div className="xhs-parking-demo__block xhs-parking-demo__block--two" />
      <div className="xhs-parking-demo__block xhs-parking-demo__block--three" />
      <div className="xhs-parking-demo__water" />

      <svg
        className="xhs-parking-demo__route"
        viewBox="0 0 1200 620"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {isHospital ? (
          isEnhanced ? (
            <path d="M116 508 C230 470 262 382 365 330 C500 260 580 276 650 224 C738 158 770 134 890 154 C984 170 1030 224 1090 190" />
          ) : (
            <path d="M116 508 C248 466 286 350 420 286 C530 234 640 216 748 192" />
          )
        ) : (
          isEnhanced ? (
            <path d="M118 504 C250 444 270 328 410 300 C556 270 650 344 770 286 C876 234 920 160 1080 178" />
          ) : (
            <path d="M118 504 C260 444 302 326 454 278 C570 242 648 230 760 214" />
          )
        )}
      </svg>

      <span className="xhs-parking-demo__vehicle"><Navigation size={34} fill="currentColor" /></span>

      {isHospital ? (
        <>
          <MapMarker
            className="xhs-parking-demo__marker--primary"
            label={isEnhanced ? "1 门诊入口落客" : "东方医院南院停车场"}
            style={{ left: "58%", top: "22%" }}
          />
          {isEnhanced ? (
            <>
              <MapMarker label="2 恒大翰城 · 周末备选" style={{ left: "72%", top: "32%" }} />
              <MapMarker label="3 云台路×尚博路" style={{ left: "87%", top: "23%" }} />
            </>
          ) : null}
        </>
      ) : (
        <>
          <MapMarker
            className="xhs-parking-demo__marker--primary"
            label="前滩太古里"
            style={{ left: "61%", top: "24%" }}
          />
          {isEnhanced ? (
            <>
              <MapMarker label="1 L+ Plaza · 周末4小时" style={{ left: "76%", top: "39%" }} />
              <MapMarker label="2 前滩公园巷 · 活动期3小时" style={{ left: "87%", top: "25%" }} />
            </>
          ) : null}
        </>
      )}

      <span className="xhs-parking-demo__map-caption">
        <Route size={26} />
        {isHospital && isEnhanced
          ? "落客点 + 顺路停车场依次尝试"
          : isEnhanced
            ? "免费时长与步行距离联合排序"
            : "按目的地距离推荐最近停车场"}
      </span>
    </div>
  );
}

function MapMarker({
  className,
  label,
  style
}: {
  className?: string;
  label: string;
  style: React.CSSProperties;
}) {
  return (
    <span className={clsx("xhs-parking-demo__marker", className)} style={style}>
      <i><ParkingCircle size={28} /></i>
      <strong>{label}</strong>
    </span>
  );
}
